import React from 'react'
import styled from '@emotion/styled'
import { Link } from 'react-router-dom'
import SubscriptionSection from './SubscriptionSection'
import { ISubscription } from '../../api/controllers/subscriptions.controller'
import { Button } from '../../components'

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
  background: #f5f5f7;
`

const Title = styled.h1`
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 30px;
  color: #21304e;
`

const UserSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #21304e;
`

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`

const InfoLabel = styled.span`
  font-size: 16px;
  color: #737991;
`

const InfoValue = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #21304e;
`

const MessageBanner = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  color: #856404;
  font-size: 16px;
  line-height: 1.5;
`

const LogoutButtonContainer = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
`

const LogoutButton = styled(Button)`
  background: #dc3545;
  color: white;
  border: none;
  max-width: 100%;

  &:hover {
    background: #c82333;
  }

  &:active {
    background: #bd2130;
  }
`

const InterviewsSection = styled(UserSection)`
  margin-bottom: 20px;
`

const InterviewList = styled.div`
  display: grid;
  gap: 12px;
`

const InterviewCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  background: #fafafa;
  transition: all 0.2s;

  &:hover {
    border-color: #2196f3;
    background: #f5f5f5;
  }
`

const InterviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`

const InterviewTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #21304e;
`

const InterviewStatus = styled.span<{ status: string }>`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ status }) => {
    if (status === 'completed' || status === 'report_provided') return '#4caf50'
    if (status === 'started' || status === 'in_interview') return '#2196f3'
    if (status === 'scheduled' || status === 'matched') return '#ff9800'
    return '#9e9e9e'
  }};
  color: white;
`

const InterviewInfo = styled.div`
  color: #737991;
  font-size: 14px;
  margin-bottom: 4px;
`

const InterviewLink = styled(Link)`
  color: #2196f3;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  display: inline-block;
  margin-top: 8px;

  &:hover {
    text-decoration: underline;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 20px;
  color: #737991;
  font-size: 14px;
`

const LoadingState = styled.div`
  text-align: center;
  padding: 20px;
  color: #737991;
  font-size: 14px;
`

interface IAccountPageProps {
  user: {
    email: string
    name: string
  }
  subscription: ISubscription | null
  interviewCount: number
  remainingInterviews: number
  onManageSubscription: () => void
  onLogout: () => void
  loading?: boolean
  message?: string | null
  interviews?: { regular_interviews: any[]; ava_interviews: any[] } | null
  interviewsLoading?: boolean
}

const AccountPage: React.FC<IAccountPageProps> = ({
  user,
  subscription,
  interviewCount,
  remainingInterviews,
  onManageSubscription,
  onLogout,
  loading = false,
  message,
  interviews,
  interviewsLoading = false,
}) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getInterviewLink = (interview: any, isAva: boolean) => {
    if (isAva) {
      return `/ava/${interview.candidate_token_code}`
    }
    return `/interviews/${interview.candidate_token_code || interview.id}`
  }

  const allInterviews = [
    ...(interviews?.regular_interviews || []).map((i) => ({ ...i, isAva: false })),
    ...(interviews?.ava_interviews || []).map((i) => ({ ...i, isAva: true })),
  ]

  return (
    <Container>
      <Title>Account</Title>

      {message && <MessageBanner>{message}</MessageBanner>}

      <UserSection>
        <SectionTitle>User Information</SectionTitle>
        <InfoRow>
          <InfoLabel>Name</InfoLabel>
          <InfoValue>{user.name}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Email</InfoLabel>
          <InfoValue>{user.email}</InfoValue>
        </InfoRow>
        <LogoutButtonContainer>
          <LogoutButton type="button" onClick={onLogout} title="Log out">
            Log out
          </LogoutButton>
        </LogoutButtonContainer>
      </UserSection>

      {interviews !== undefined && (
        <InterviewsSection>
          <SectionTitle>Interviews This Month</SectionTitle>
          {interviewsLoading ? (
            <LoadingState>Loading interviews...</LoadingState>
          ) : allInterviews.length === 0 ? (
            <EmptyState>You don't have any interviews this month.</EmptyState>
          ) : (
            <InterviewList>
              {allInterviews.map((interview) => (
                <InterviewCard key={`${interview.isAva ? 'ava' : 'regular'}-${interview.id}`}>
                  <InterviewHeader>
                    <InterviewTitle>
                      {interview.isAva
                        ? interview.ava_position?.title || `AVA Interview #${interview.order_number}`
                        : interview.desired_position || `Interview #${interview.order_number}`}
                    </InterviewTitle>
                    <InterviewStatus status={interview.status}>
                      {interview.status || 'pending'}
                    </InterviewStatus>
                  </InterviewHeader>
                  {interview.isAva ? (
                    <>
                      {interview.ava_position?.company && (
                        <InterviewInfo>Company: {interview.ava_position.company}</InterviewInfo>
                      )}
                      {interview.created_at && (
                        <InterviewInfo>Created: {formatDate(interview.created_at)}</InterviewInfo>
                      )}
                    </>
                  ) : (
                    <>
                      {interview.position_company && (
                        <InterviewInfo>Company: {interview.position_company}</InterviewInfo>
                      )}
                      {interview.scheduled_time && (
                        <InterviewInfo>
                          Scheduled: {formatDate(interview.scheduled_time)}
                        </InterviewInfo>
                      )}
                      {interview.created_at && (
                        <InterviewInfo>Created: {formatDate(interview.created_at)}</InterviewInfo>
                      )}
                    </>
                  )}
                  {interview.candidate_token_code && (
                    <InterviewLink to={getInterviewLink(interview, interview.isAva)}>
                      View Results →
                    </InterviewLink>
                  )}
                </InterviewCard>
              ))}
            </InterviewList>
          )}
        </InterviewsSection>
      )}

      <SubscriptionSection
        subscription={subscription}
        interviewCount={interviewCount}
        remainingInterviews={remainingInterviews}
        onManageSubscription={onManageSubscription}
        loading={loading}
      />
    </Container>
  )
}

export default AccountPage

